import { withStyles, Card, Box, BodyText } from 'vopay';
import { useTranslation, Trans } from 'react-i18next';
import { CircleButton, InfoBanner, MaterialSymbol } from '#shared/components/UI';
import Dialog from '#shared/components/Dialog/Dialog';
import styles from './HowWebhooksWorkDialog.module.scss';

const cn = withStyles(styles);

type HowWebhooksWorkDialogProps = {
  onClose: () => void;
};

export default function HowWebhooksWorkDialog({ onClose }: HowWebhooksWorkDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Webhooks.HowItWorksDialog' });
  const { t: tHowItWorks } = useTranslation('translations', { keyPrefix: 'Shared.Webhooks.HowItWorks' });
  const { t: tRequest } = useTranslation('translations', { keyPrefix: 'Shared.Webhooks.TheRequest' });

  const steps = [
    tHowItWorks('attempt1', {
      defaultValue:
        'On the first attempt, the system waits up to 5 seconds for a 2XX HTTP status code to mark the notification as successful.',
    }),
    tHowItWorks('attempt2', {
      defaultValue:
        'If the attempt times out or fails, the system retries after 5 minutes, waiting up to 15 seconds for a successful response.',
    }),
    tHowItWorks('attempt3', {
      defaultValue: 'If that retry fails, it attempts one final time after 5 more minutes, waiting up to 30 seconds.',
    }),
    tHowItWorks('failed', {
      defaultValue: 'If the third attempt fails, the notification is marked as failed and no further retries are made.',
    }),
  ];

  return (
    <Card className={cn('dialog')}>
      <Box jc="sb" ai="center" className={cn('header')}>
        <Dialog.Title level="3">{t('title', { defaultValue: 'How Webhooks Work' })}</Dialog.Title>
        <CircleButton icon="close" size="large" onClick={onClose} />
      </Box>

      <div className={cn('body')}>
        <BodyText weight="md">
          {tHowItWorks('intro', {
            defaultValue:
              'To receive notifications, you must configure a valid callback URL. Once configured, the system will begin sending webhook events.',
          })}
        </BodyText>

        <ol className={cn('steps')}>
          {steps.map((step, index) => (
            <li key={index} className={cn('step')}>
              <span className={cn('indicator')}>
                <MaterialSymbol icon="check_circle" size="14px" className={cn('checkIcon')} />
              </span>

              <BodyText className={cn('stepText')}>{step}</BodyText>
            </li>
          ))}
        </ol>

        <InfoBanner>
          <Trans
            t={tRequest}
            i18nKey="description"
            defaults="All webhook requests are sent in JSON format. Please see our <a>developer documentation</a> for details."
            components={{
              a: (
                <a className={cn('link')} href="https://docs.vopay.com/docs/events" target="_blank" rel="noreferrer" />
              ),
            }}
          />
        </InfoBanner>
      </div>
    </Card>
  );
}
